import { ConnectedPosition, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';

// Reusable hover popover backed by CDK Overlay + a TemplatePortal. This is the
// replacement for the ng-zorro `nz-popover [nzPopoverContent]="tpl"` pattern
// (rich, template-driven hover tooltips) — MatTooltip can only render plain
// text. It mirrors the proven CDK hover pattern already used by
// MaterialHoverTriggerDirective (materials feature), but takes a TemplateRef
// instead of a fixed component, so any feature can pass its own `<ng-template>`.
//
// NOTE: `[mdPopover]` is already taken by the app's click-dropdown directive
// (features/shared/directives/popover), hence the distinct `[mdHoverPopover]`
// selector.

const SHOW_DELAY_MS = 100;
const HIDE_DELAY_MS = 150;

type Placement = 'top' | 'right' | 'bottom' | 'left';

// 'hover' opens on pointer/keyboard focus (the common case). 'manual' hands
// control to the host component via [mdHoverPopoverVisible] — needed for the
// time input's coach-mark, which the old markup drove with
// `[nzPopoverTrigger]="null"` and toggled from keydown/outside-click.
type Trigger = 'hover' | 'manual';

const POSITIONS: Record<Placement, ConnectedPosition> = {
  top: {
    originX: 'center',
    originY: 'top',
    overlayX: 'center',
    overlayY: 'bottom',
    offsetY: -8,
  },
  bottom: {
    originX: 'center',
    originY: 'bottom',
    overlayX: 'center',
    overlayY: 'top',
    offsetY: 8,
  },
  right: {
    originX: 'end',
    originY: 'center',
    overlayX: 'start',
    overlayY: 'center',
    offsetX: 8,
  },
  left: {
    originX: 'start',
    originY: 'center',
    overlayX: 'end',
    overlayY: 'center',
    offsetX: -8,
  },
};

@Directive({
  selector: '[mdHoverPopover]',
  exportAs: 'mdHoverPopover',
})
export class HoverPopoverDirective implements OnDestroy {
  // The template rendered inside the overlay panel. The directive is a no-op
  // while unset, so it is safe to apply unconditionally.
  @Input('mdHoverPopover') set content(value: TemplateRef<unknown> | null) {
    this.content_ = value;
    // A manual host may bind visibility before the template resolves; re-sync
    // so the panel still opens once content arrives.
    this.syncManual();
  }
  get content(): TemplateRef<unknown> | null {
    return this.content_;
  }

  @Input() mdHoverPopoverPlacement: Placement = 'top';

  // Optional extra class applied to the overlay panel (maps the old
  // nzPopoverOverlayClassName), on top of the base `md-hover-popover` box.
  @Input() mdHoverPopoverClass = '';

  @Input() mdHoverPopoverTrigger: Trigger = 'hover';

  // Only honoured while trigger is 'manual'.
  @Input() set mdHoverPopoverVisible(visible: boolean) {
    this.manualVisible = visible;
    this.syncManual();
  }

  private content_: TemplateRef<unknown> | null = null;
  private manualVisible = false;
  private overlayRef: OverlayRef | null = null;
  private showTimer: ReturnType<typeof setTimeout> | null = null;
  private hideTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly host: ElementRef<HTMLElement>,
    private readonly overlay: Overlay,
    private readonly viewContainerRef: ViewContainerRef,
  ) {}

  @HostListener('mouseenter')
  onEnter(): void {
    if (this.mdHoverPopoverTrigger !== 'hover' || !this.content) return;
    this.cancelHide();
    this.showTimer = setTimeout(() => this.show(), SHOW_DELAY_MS);
  }

  @HostListener('mouseleave')
  onLeave(): void {
    if (this.mdHoverPopoverTrigger !== 'hover') return;
    this.cancelShow();
    this.scheduleHide();
  }

  // Keyboard parity with the nz-popover this replaces: several call sites put
  // the directive on a focusable <button>, so pointer-only triggers would leave
  // the content unreachable without a mouse. focusin/focusout (rather than
  // focus/blur) so a focusable child also opens the panel.
  @HostListener('focusin')
  onFocus(): void {
    if (this.mdHoverPopoverTrigger !== 'hover' || !this.content) return;
    this.cancelHide();
    this.show();
  }

  @HostListener('focusout')
  onBlur(): void {
    if (this.mdHoverPopoverTrigger !== 'hover') return;
    this.cancelShow();
    this.scheduleHide();
  }

  ngOnDestroy(): void {
    this.cancelShow();
    this.cancelHide();
    this.hide();
  }

  private show(): void {
    if (!this.content || this.overlayRef) return;
    // A hide scheduled moments ago must not fire onto the panel we are about to
    // open, or a quick close/reopen inside HIDE_DELAY_MS kills the new panel.
    this.cancelHide();

    const panelClass = ['md-hover-popover'];
    if (this.mdHoverPopoverClass) panelClass.push(this.mdHoverPopoverClass);

    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this.host)
      .withPositions([
        POSITIONS[this.mdHoverPopoverPlacement],
        // sensible fallback so the panel stays on screen
        POSITIONS.bottom,
        POSITIONS.top,
      ])
      .withFlexibleDimensions(false)
      .withPush(true);

    const ref = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: false,
      panelClass,
    });
    this.overlayRef = ref;

    if (this.mdHoverPopoverTrigger === 'hover') {
      // Keep the panel open while the pointer is over it (interactivity), close
      // shortly after it leaves — matches nz-popover hover behavior.
      ref.overlayElement.addEventListener('mouseenter', () => this.cancelHide());
      ref.overlayElement.addEventListener('mouseleave', () => this.scheduleHide());

      // Escape closes, scoped to this overlay rather than a per-instance
      // `document:keydown` host listener: keydownEvents() only fires while the
      // panel is attached, so N idle directives cost nothing. In 'manual' mode
      // the host owns visibility — closing here would leave its flag stuck
      // `true` and the panel unreopenable, so we deliberately don't bind it.
      ref.keydownEvents().subscribe(event => {
        if (event.key !== 'Escape') return;
        this.cancelShow();
        this.hide();
      });
    }

    ref.attach(new TemplatePortal(this.content, this.viewContainerRef));
  }

  private hide(): void {
    // Drop any pending hide so it cannot fire onto a later panel.
    this.cancelHide();
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
  }

  // show()/hide() are idempotent, so this is safe to call from any input setter
  // on every change detection pass.
  private syncManual(): void {
    if (this.mdHoverPopoverTrigger !== 'manual') return;
    // Losing the template while open must close the panel, not leave it showing
    // stale content — show() would early-return on the null and change nothing.
    this.manualVisible && this.content ? this.show() : this.hide();
  }

  private scheduleHide(): void {
    this.cancelHide();
    this.hideTimer = setTimeout(() => this.hide(), HIDE_DELAY_MS);
  }

  private cancelShow(): void {
    if (this.showTimer) {
      clearTimeout(this.showTimer);
      this.showTimer = null;
    }
  }

  private cancelHide(): void {
    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
  }
}
